import React from "react";
import DutchAddressFields from "../../../components/DutchAddressFields";
import {
  FiArrowLeft,
  FiEdit3,
  FiHome,
  FiMail,
  FiPhone,
  FiSave,
  FiUser,
} from "react-icons/fi";

const ArrowLeftIcon = FiArrowLeft;
const EditIcon = FiEdit3;
const HomeIcon = FiHome;
const MailIcon = FiMail;
const PhoneIcon = FiPhone;
const SaveIcon = FiSave;
const UserIcon = FiUser;

const getNewsletterLabel = ({ t, status, optedOut }) => {
  if (optedOut) return t("parentPortal.newsletter.opted_out", { defaultValue: "Unsubscribed" });
  if (status === "SUBSCRIBED") return t("parentPortal.newsletter.subscribed", { defaultValue: "Subscribed" });
  if (status === "UNSUBSCRIBED") return t("parentPortal.newsletter.unsubscribed", { defaultValue: "Unsubscribed" });
  if (status === "PENDING") return t("parentPortal.newsletter.pending", { defaultValue: "Pending" });
  return t("parentPortal.newsletter.unknown", { defaultValue: "Unknown" });
};

const DetailRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 rounded-2xl px-1 py-2">
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900/50" aria-hidden="true"><Icon /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</span>
        <span className="mt-0.5 block whitespace-pre-line break-words text-base font-semibold text-slate-900 dark:text-slate-100">{value}</span>
      </span>
      <style>{`
        @keyframes myDetailsSlideIn {
          from { opacity: 0; transform: translateX(32px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>

    </div>
  );
};

const TextField = ({ label, name, value, onChange, type = "text", full = false }) => (
  <label className={`flex min-w-0 flex-col gap-1.5 ${full ? "sm:col-span-2" : ""}`}>
    <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">{label}</span>
    <input className="min-h-[46px] w-full rounded-2xl border border-slate-200 bg-white/80 px-4 text-base font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100 dark:focus:ring-emerald-900/30" type={type} name={name} value={value || ""} onChange={onChange} />
  </label>
);

const MyDetailsScreen = ({
  t,
  parentData,
  form,
  handleChange,
  handleSave,
  editMode,
  setEditMode,
  fullName,
  newsletterStatus,
  isNewsletterEligible,
  onBack,
}) => {
  const address = [parentData?.address, parentData?.zipCode, parentData?.city].filter(Boolean).join("\n");
  const newsletterLabel = getNewsletterLabel({ t, status: newsletterStatus, optedOut: parentData?.newsletterOptOut });

  const handleBack = () => {
    setEditMode?.(false);
    onBack?.();
  };

  const submit = (event) => {
    event.preventDefault();
    handleSave?.();
    setEditMode?.(false);
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5 text-left motion-safe:animate-[myDetailsSlideIn_420ms_cubic-bezier(0.22,1,0.36,1)_both]">
      <header className="flex flex-col gap-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white p-5 ring-1 ring-emerald-100 dark:from-emerald-950/20 dark:to-slate-950/20 dark:ring-emerald-900/50 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <button
            type="button"
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-emerald-100 bg-white text-emerald-800 shadow-sm transition hover:-translate-x-0.5 hover:border-emerald-200 hover:bg-emerald-50 dark:border-emerald-900/60 dark:bg-slate-900 dark:text-emerald-200 dark:hover:bg-emerald-950/30"
            onClick={handleBack}
            aria-label={t("common.back", { defaultValue: "Back" })}
          >
            <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-sm" aria-hidden="true"><UserIcon /></span>
          <span className="min-w-0">
            <h2 className="text-2xl font-black tracking-tight text-slate-950 dark:text-slate-50">{t("parentPortal.my_details", { defaultValue: "My details" })}</h2>
            <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">{t("parentPortal.my_details_subtitle", { defaultValue: "Contact, family and payment details" })}</p>
          </span>
        </div>

        {!editMode && (
          <button type="button" className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-full bg-emerald-600 px-5 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-emerald-500 hover:shadow-md dark:bg-emerald-600 dark:hover:bg-emerald-500" onClick={() => setEditMode?.(true)}>
            <EditIcon aria-hidden="true" />
            {t("common.actions.edit", { defaultValue: "Edit" })}
          </button>
        )}
      </header>

      <section className="rounded-[2rem] border border-slate-200/80 bg-white/95 p-5 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/70 sm:p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h3 className="text-lg font-black text-slate-950 dark:text-slate-50">{t("parentPortal.personal_details", { defaultValue: "Personal details" })}</h3>

        </div>

        {!editMode ? (
          <div className="space-y-2">
            <DetailRow icon={UserIcon} label={t("parentPortal.full_name", { defaultValue: "Name" })} value={fullName} />
            <DetailRow icon={PhoneIcon} label={t("parentPortal.phone", { defaultValue: "Phone" })} value={parentData?.phone} />
            <DetailRow icon={MailIcon} label={t("parentPortal.email", { defaultValue: "Email" })} value={parentData?.email} />
            <DetailRow icon={HomeIcon} label={t("parentPortal.address", { defaultValue: "Address" })} value={address} />
            {isNewsletterEligible && (
              <DetailRow icon={MailIcon} label={t("parentPortal.newsletter.label", { defaultValue: "Newsletter" })} value={newsletterLabel} />
            )}
          </div>
        ) : (
          <form className="space-y-4" onSubmit={submit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField label={t("parentPortal.first_name", { defaultValue: "First name" })} name="firstName" value={form?.firstName} onChange={handleChange} />
              <TextField label={t("parentPortal.last_name", { defaultValue: "Last name" })} name="lastName" value={form?.lastName} onChange={handleChange} />
              <TextField label={t("parentPortal.phone", { defaultValue: "Phone" })} name="phone" value={form?.phone} onChange={handleChange} full />
              <TextField label={t("parentPortal.email", { defaultValue: "Email" })} name="email" value={form?.email} onChange={handleChange} type="email" full />
              <DutchAddressFields
                address={form?.address || ""}
                zipCode={form?.zipCode || ""}
                city={form?.city || ""}
                onChange={(field, value) => handleChange({ target: { name: field, type: "text", value } })}
                t={t}
                inputClassName="min-h-[46px] w-full rounded-2xl border border-slate-200 bg-white/80 px-4 text-base font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100 dark:focus:ring-emerald-900/30"
                labelClassName="flex min-w-0 flex-col gap-1.5 text-sm font-semibold text-slate-600 dark:text-slate-300"
                addressClassName="sm:col-span-2"
              />
            </div>

            <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
              <button type="button" className="inline-flex min-h-[44px] items-center justify-center rounded-2xl border border-slate-200 bg-white/80 px-5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950/30 dark:text-slate-200" onClick={() => setEditMode?.(false)}>
                {t("common.cancel", { defaultValue: "Cancel" })}
              </button>
              <button type="submit" className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60">
                <SaveIcon aria-hidden="true" />
                {t("common.save", { defaultValue: "Save" })}
              </button>
            </div>
          </form>
        )}
      </section>

    </div>
  );
};

export default MyDetailsScreen;
